import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MessagesSquare, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';

import { PeriodControl } from '../../../components/design-system/analytics/PeriodControl';
import { PageContainer } from '../../../common/components/PageContainer';
import { ShopperOverview, ShopperQuestions, useShopperInsightsApi } from '../../../common/hooks/use-shopper-insights-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { PlanMeters } from '../../../components/design-system/plan/PlanMeters';
import { HistoryLimitNotice } from '../../../components/design-system/plan/HistoryLimitNotice';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';
import { KpiCard } from '../../../components/design-system/admin/KpiCard';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { formatNumber } from '../../../components/design-system/admin/format';
import { BarList } from '../../../components/design-system/analytics/BarList';
import { DistributionBar } from '../../../components/design-system/analytics/DistributionBar';
import { Insight, InsightList } from '../../../components/design-system/analytics/InsightList';
import { MiniStat } from '../../../components/design-system/analytics/TrendChart';
import { TaskItem, TaskList } from '../../../components/design-system/analytics/TaskList';
import { formatCost, formatPercent, formatSeconds, useInsightsRange } from './assistant-shared';
import {
    ReadingItem,
    assistantReading,
    daysIn,
    groupUnanswered,
    outcomeSplit,
    previousRange,
    themeRows,
    unansweredRate,
} from './assistant-reading';
import { AssistantHeader, useAskArdiAbout, useOutcomeLabel, useReasonLabel, useThemeLabel } from './assistant-ui';
import { ReturningVisitorsCard } from './returning-visitors-card';

/**
 * The Shopping Assistant's dashboard, read like a note from Ardi.
 *
 * It opens with sentences — how many shoppers the assistant helped, what it
 * could not answer, the one question worth fixing — then four numbers against
 * the period before, how conversations ended, what shoppers ask about, and
 * the short list of what needs the vendor. The raw counters (AI cost, reply
 * time, how chats start) are still there, folded under "More detail".
 */
const OverviewBody = () => {
    const { t } = useTranslation();
    const api = useShopperInsightsApi();
    const { range, periodProps } = useInsightsRange();
    const outcomeLabel = useOutcomeLabel();
    const themeLabel = useThemeLabel();
    const reasonLabel = useReasonLabel();
    const askArdi = useAskArdiAbout();

    const [data, setData] = useState<ShopperOverview | null>(null);
    const [previous, setPrevious] = useState<ShopperOverview | null>(null);
    const [questions, setQuestions] = useState<ShopperQuestions | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        Promise.all([
            api.getOverview(range),
            // The comparison and the questions are extras: without them the page still reads.
            api.getOverview(previousRange(range)).catch(() => null),
            api.getQuestions(range).catch(() => null),
        ])
            .then(([current, before, asked]) => {
                if (cancelled) return;
                setData(current);
                setPrevious(before);
                setQuestions(asked);
            })
            .catch((error) => {
                console.error('Error loading assistant overview:', error);
                if (!cancelled) { setData(null); setFailed(true); }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate]);

    const days = daysIn(range);
    const totals = data?.totals;
    const before = previous?.totals;
    const reading = useMemo(() => assistantReading({ overview: data, previous, questions }), [data, previous, questions]);
    const split = outcomeSplit(data);
    const themes = themeRows(questions);
    const needs = groupUnanswered(questions?.unanswered, 20);
    const quiet = !loading && !!totals && totals.conversations === 0;

    const readingText = (item: ReadingItem): Insight => {
        const params: Record<string, string | number> = { ...item.params };
        if (typeof params.theme === 'string') params.theme = themeLabel(params.theme);
        const action = item.action;
        return {
            id: item.id,
            tone: item.tone,
            title: t(`SHOPPER_UI.READING.${item.title}`, params),
            detail: item.detail ? t(`SHOPPER_UI.READING.${item.detail}`, params) : undefined,
            action: !action
                ? undefined
                : action.kind === 'link'
                    ? { label: t(`SHOPPER_UI.READING.ACTION_${action.label}`), href: action.href }
                    : askArdi
                        ? { label: t('SHOPPER_UI.ASK_ARDI.LABEL'), onClick: () => askArdi(action.question, action.sku) }
                        : { label: t('SHOPPER_UI.READING.ACTION_REVIEW'), href: '/assistant/questions' },
        };
    };

    const tasks: TaskItem[] = needs.map((item) => ({
        id: item.key,
        tone: 'watch',
        title: item.question ? `“${item.question}”` : t('SHOPPER_UI.COMMON.NO_QUESTION'),
        detail: reasonLabel(item.reason),
        meta: item.count > 1 ? t('SHOPPER_UI.COMMON.ASKED_TIMES', { count: item.count }) : undefined,
        href: `/assistant/conversations?open=${item.threadUuid}`,
        action: askArdi && item.question && item.reason === 'no_match'
            ? { label: t('SHOPPER_UI.ASK_ARDI.LABEL'), onClick: () => askArdi(item.question, item.sku) }
            : undefined,
    }));

    const rate = unansweredRate(totals);
    const previousRate = unansweredRate(before);
    const vsBefore = t('SHOPPER_UI.COMMON.VS_PREVIOUS', { count: days });

    // How chats start and on which devices, in the words the old dashboard used for them.
    const labelled = (map: Record<string, number> | undefined) =>
        Object.entries(map || {})
            .map(([key, value]) => ({ key, label: t(`SHOPPING_ASSISTANT.LABELS.${key.toUpperCase()}`, { defaultValue: key }), value }))
            .sort((a, b) => b.value - a.value);

    return (
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6">
            <AssistantHeader
                title={t('SHOPPER_UI.DASHBOARD.TITLE')}
                subtitle={t('SHOPPER_UI.DASHBOARD.SUBTITLE')}
                actions={<PeriodControl {...periodProps} />}
            />

            <HistoryLimitNotice period={range} />
            {/* The chats the plan allows this month — drawn only when it meters them. */}
            <PlanMeters keys={['maxShopperChatsPerMonth']} testId="assistant-plan-meters" />

            {data?.partial && <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.COMMON.PARTIAL')}</p>}

            {loading ? (
                <Skeleton className="h-40 w-full rounded-xl" />
            ) : failed ? (
                <EmptyState
                    title={t('SHOPPER_UI.COMMON.FAILED_TITLE')}
                    description={t('SHOPPER_UI.COMMON.FAILED_TEXT')}
                />
            ) : quiet ? null : (
                <InsightList
                    title={t('SHOPPER_UI.READING.TITLE')}
                    description={t('SHOPPER_UI.COMMON.LAST_DAYS', { count: days })}
                    insights={reading.map(readingText)}
                    testId="assistant-reading"
                />
            )}

            {quiet && (
                <EmptyState
                    icon={<MessagesSquare />}
                    title={t('SHOPPER_UI.DASHBOARD.QUIET_TITLE')}
                    description={t('SHOPPER_UI.DASHBOARD.QUIET_TEXT')}
                    action={(
                        <Button variant="outline" size="sm" asChild>
                            <Link to="/assistant/settings"><Settings2 />{t('SHOPPER_UI.DASHBOARD.OPEN_SETTINGS')}</Link>
                        </Button>
                    )}
                />
            )}

            {!quiet && !failed && (
                <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <KpiCard
                            label={t('SHOPPER_UI.DASHBOARD.KPI_CONVERSATIONS')}
                            value={formatNumber(totals?.conversations ?? 0)}
                            delta={totals && before ? { current: totals.conversations, previous: before.conversations } : undefined}
                            hint={vsBefore}
                            trend={data?.series?.map((d) => d.conversations)}
                            href="/assistant/conversations"
                            loading={loading}
                        />
                        <KpiCard
                            label={t('SHOPPER_UI.DASHBOARD.KPI_SHOPPERS')}
                            value={formatNumber(totals?.shoppers ?? 0)}
                            delta={totals && before ? { current: totals.shoppers, previous: before.shoppers } : undefined}
                            hint={vsBefore}
                            href="/assistant/conversations"
                            loading={loading}
                        />
                        <KpiCard
                            label={t('SHOPPER_UI.DASHBOARD.KPI_CART')}
                            value={formatNumber(totals?.cartClicks ?? 0)}
                            delta={totals && before ? { current: totals.cartClicks, previous: before.cartClicks } : undefined}
                            hint={totals ? t('SHOPPER_UI.DASHBOARD.KPI_CART_HINT', { count: totals.productClicks }) : undefined}
                            href="/assistant/conversations"
                            loading={loading}
                        />
                        <KpiCard
                            label={t('SHOPPER_UI.DASHBOARD.KPI_UNANSWERED')}
                            value={formatPercent(rate)}
                            delta={rate !== null && previousRate !== null ? { current: rate * 100, previous: previousRate * 100 } : undefined}
                            invert
                            hint={totals ? t('SHOPPER_UI.DASHBOARD.KPI_UNANSWERED_HINT', { count: totals.unanswered }) : undefined}
                            href="/assistant/conversations?outcome=unanswered"
                            loading={loading}
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <SectionCard
                            title={t('SHOPPER_UI.DASHBOARD.OUTCOMES_TITLE')}
                            description={split
                                ? t(split.unit === 'conversations' ? 'SHOPPER_UI.DASHBOARD.OUTCOMES_DESC' : 'SHOPPER_UI.DASHBOARD.OUTCOMES_DESC_REPLIES', { count: split.total })
                                : undefined}
                        >
                            {loading || !split ? (
                                <Skeleton className="h-24 w-full" />
                            ) : (
                                <DistributionBar
                                    columns={2}
                                    segments={split.segments.map((s) => ({
                                        key: s.key,
                                        label: outcomeLabel(s.key),
                                        value: s.value,
                                        tone: s.key === 'unanswered' ? 'watch' : undefined,
                                    }))}
                                />
                            )}
                        </SectionCard>

                        <SectionCard
                            title={t('SHOPPER_UI.DASHBOARD.THEMES_TITLE')}
                            description={t('SHOPPER_UI.DASHBOARD.THEMES_DESC')}
                            action={(
                                <Button variant="ghost" size="sm" asChild>
                                    <Link to="/assistant/questions">{t('SHOPPER_UI.COMMON.SEE_ALL')}</Link>
                                </Button>
                            )}
                        >
                            {loading ? (
                                <Skeleton className="h-24 w-full" />
                            ) : (
                                <BarList
                                    emptyText={t('SHOPPER_UI.DASHBOARD.THEMES_EMPTY')}
                                    items={themes.slice(0, 6).map((theme) => ({
                                        key: theme.theme,
                                        label: themeLabel(theme.theme),
                                        value: theme.count,
                                        secondary: theme.unanswered > 0
                                            ? t('SHOPPER_UI.COMMON.NOT_ANSWERED_COUNT', { count: theme.unanswered })
                                            : undefined,
                                        href: `/assistant/questions?theme=${theme.theme}`,
                                    }))}
                                />
                            )}
                        </SectionCard>
                    </div>

                    <ReturningVisitorsCard range={range} />

                    <TaskList
                        title={t('SHOPPER_UI.NEEDS.TITLE')}
                        description={t('SHOPPER_UI.NEEDS.DESC')}
                        items={tasks}
                        loading={loading}
                        max={5}
                        emptyTitle={t('SHOPPER_UI.NEEDS.EMPTY_TITLE')}
                        emptyText={t('SHOPPER_UI.NEEDS.EMPTY_TEXT')}
                        more={{ label: () => t('SHOPPER_UI.NEEDS.SEE_ALL'), href: '/assistant/questions' }}
                        testId="assistant-needs-you"
                    />

                    {totals && (
                        <Collapsible className="rounded-xl border bg-card">
                            <CollapsibleTrigger className="group/more flex w-full items-center justify-between gap-3 px-6 py-4 text-start">
                                <span>
                                    <span className="block text-sm font-medium">{t('SHOPPER_UI.DASHBOARD.MORE_TITLE')}</span>
                                    <span className="mt-0.5 block text-xs text-muted-foreground">{t('SHOPPER_UI.DASHBOARD.MORE_DESC')}</span>
                                </span>
                                <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/more:rotate-180" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="border-t px-6 py-5">
                                <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                                    <MiniStat
                                        label={t('SHOPPER_UI.DASHBOARD.PROMPT_RATE')}
                                        value={formatPercent(totals.promptClickRate)}
                                        sub={t('SHOPPER_UI.DASHBOARD.PROMPT_RATE_SUB', { clicks: formatNumber(totals.promptClicks), shown: formatNumber(totals.promptImpressions) })}
                                    />
                                    <MiniStat
                                        label={t('SHOPPER_UI.DASHBOARD.FEEDBACK')}
                                        value={`${formatNumber(totals.feedbackUp)} / ${formatNumber(totals.feedbackDown)}`}
                                        sub={t('SHOPPER_UI.DASHBOARD.FEEDBACK_SUB')}
                                    />
                                    <MiniStat label={t('SHOPPER_UI.DASHBOARD.REPLY_TIME')} value={formatSeconds(totals.avgLatencyMs)} />
                                    <MiniStat label={t('SHOPPER_UI.DASHBOARD.AI_COST')} value={formatCost(totals.costUsd)} />
                                </div>
                                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.DASHBOARD.ENTRIES')}</p>
                                        <BarList emptyText={t('SHOPPER_UI.COMMON.NO_DATA')} items={labelled(data?.entries)} />
                                    </div>
                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.DASHBOARD.DEVICES')}</p>
                                        <BarList emptyText={t('SHOPPER_UI.COMMON.NO_DATA')} items={labelled(data?.devices)} />
                                    </div>
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    )}
                </>
            )}
        </div>
    );
};

export const AssistantOverviewPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.SHOPPING_ASSISTANT.TITLE', 'Shopping Assistant') }, { label: t('SIDEBAR.DASHBOARD.TITLE') }]}>
            <FeatureGate feature="shopperChat">
                <OverviewBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AssistantOverviewPage;
